import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Card, CardContent } from '../../../components/ui/Card';
import { useToast } from '../../../components/ui/Toast';
import { KIND_TEXT, approverProblem, cleanEmail, comboKey, comboLabel, groupCombos, natureOptions, specificity, subcategoryOptions } from '../routing';
import { personOf } from '../approverDetails';
import ApproverDetailsDrawer from './ApproverDetailsDrawer';

const selectClass = 'w-full h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';
const inputClass = 'w-full min-w-0 h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';
const ROLE_TEXT = { ADMIN: 'Administrator', COORDINATOR: 'Coordinator', USER: 'User' };
const LEVEL_TEXT = ['Everything else', 'Nature only', 'Sub-category only', 'Nature + sub-category'];
const BY_EMAIL = '__email__';
const EMAIL_NOTE = 'E-mail only · decides through a link, no VMS login';

// "Rahul (HR)" → "RH": the first letter of each word, punctuation skipped.
const initials = (name, email) =>
  (name || email || '?')
    .split(/\s+/)
    .map((w) => w.match(/[A-Za-z0-9]/)?.[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

/** A list of people to tick, with a search box once the list is long. */
const PeoplePicker = ({ people, selected, onToggle, disabledIds = [], label }) => {
  const [q, setQ] = useState('');
  const shown = people.filter((p) => `${p.full_name} ${p.email}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div>
      {people.length > 6 && (
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search people…"
          aria-label={`${label}: search people`}
          className="mb-2 w-full h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        />
      )}
      <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100" role="group" aria-label={label}>
        {shown.length === 0 && <p className="px-3 py-2 text-sm text-slate-500">No one matches.</p>}
        {shown.map((p) => {
          const already = disabledIds.includes(p.id);
          const checked = already || selected.includes(p.id);
          return (
            <label key={p.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${already ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
              <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary" checked={checked} disabled={already} onChange={() => onToggle(p.id)} />
              <span className="min-w-0 flex-1">
                <span className="font-medium text-slate-900">{p.full_name}</span>
                <span className="text-slate-500"> · {p.email}</span>
              </span>
              <span className="text-xs text-slate-500">{already ? 'Already in' : ROLE_TEXT[p.role] || p.role}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Someone without a VMS account, by address: type it, press Add (or Enter). Addresses already in
 * the combination, or already typed, are refused on the spot.
 */
const EmailAdder = ({ taken, onAdd, label, autoFocus = false }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);
  const add = () => {
    const email = cleanEmail(value);
    if (!email) return setError('Enter a valid e-mail address');
    if (taken.includes(email)) return setError('Already in this combination');
    onAdd(email);
    setValue('');
    setError(null);
  };
  return (
    <div>
      <div className="flex gap-2">
        <input
          type="email"
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="name@company.com"
          aria-label={label}
          autoFocus={autoFocus}
          className={inputClass}
        />
        <Button type="button" variant="outline" className="h-9 flex-shrink-0" onClick={add} disabled={!value.trim()} iconName="Plus" iconPosition="left">Add</Button>
      </div>
      {error && <p className="mt-1 text-xs text-red-600" role="alert">{error}</p>}
    </div>
  );
};

/** "New combination": nature + sub-category, and the people who approve it (VMS users and/or addresses). */
const NewCombination = ({ kind, combos, approvers, onCancel, onSave }) => {
  const [nature, setNature] = useState('');
  const [sub, setSub] = useState('');
  const [picked, setPicked] = useState([]);
  const [emails, setEmails] = useState([]);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  useEffect(() => { ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); ref.current?.querySelector('select')?.focus(); }, []);

  const existing = combos.find((c) => c.key === comboKey(nature, sub));
  const inIt = existing ? existing.people.map((p) => p.approverId).filter((id) => id != null) : [];
  const emailsInIt = existing ? existing.people.map((p) => p.email).filter(Boolean) : [];
  const toggle = (id) => setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const readyUsers = picked.filter((id) => !inIt.includes(id));
  const readyEmails = emails.filter((e) => !emailsInIt.includes(e));
  const count = readyUsers.length + readyEmails.length;

  const save = async () => {
    setSaving(true);
    try {
      const ok = await onSave(nature, sub, readyUsers, readyEmails);
      if (ok) {
        setPicked([]);
        setEmails([]);
        setNature('');
        setSub('');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={ref} className="rounded-xl border border-primary/30 bg-primary/5 p-4 sm:p-5" data-testid={`new-combination-${kind.toLowerCase()}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Icon name="PlusCircle" size={16} className="text-primary" />New combination</h3>
        <button type="button" onClick={onCancel} className="p-1 rounded text-slate-500 hover:text-slate-800 hover:bg-white" aria-label="Close new combination">
          <Icon name="X" size={16} />
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Nature of business</span>
            <select className={selectClass} value={nature} onChange={(e) => setNature(e.target.value)} aria-label={`${KIND_TEXT[kind].title}: nature of business`}>
              {natureOptions().map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Supplier sub-category</span>
            <select className={selectClass} value={sub} onChange={(e) => setSub(e.target.value)} aria-label={`${KIND_TEXT[kind].title}: sub-category`}>
              {subcategoryOptions(kind).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <p className="text-xs text-slate-600 rounded-lg bg-white/70 border border-slate-200 px-3 py-2">
            <span className="font-semibold text-slate-800">{comboLabel(nature, sub)}</span>
            {existing ? (
              <> already exists with {existing.people.length} {existing.people.length === 1 ? 'person' : 'people'} — the people you add join it.</>
            ) : (
              <> — a new combination.</>
            )}
          </p>
        </div>
        <div className="space-y-4">
          <div>
            <span className="block text-xs font-semibold text-slate-600 mb-1">VMS users <span className="font-normal text-slate-500">(approve in VMS)</span></span>
            {approvers.length === 0 ? (
              <p className="text-sm text-slate-600 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-3">
                No Coordinators or Administrators yet. Add people by e-mail below, or add a user with one of those roles.
              </p>
            ) : (
              <PeoplePicker people={approvers} selected={picked} onToggle={toggle} disabledIds={inIt} label={`${KIND_TEXT[kind].title}: people`} />
            )}
          </div>
          <div>
            <span className="block text-xs font-semibold text-slate-600 mb-1">
              People without a VMS account <span className="font-normal text-slate-500">(approve through a link in their e-mail)</span>
            </span>
            <EmailAdder taken={[...emails, ...emailsInIt]} onAdd={(e) => setEmails((cur) => [...cur, e])} label={`${KIND_TEXT[kind].title}: e-mail address`} />
            {emails.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Addresses to add">
                {emails.map((e) => (
                  <li key={e} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white pl-2.5 pr-1 py-0.5 text-xs text-slate-800">
                    <Icon name="Mail" size={12} className="text-slate-500" />{e}
                    <button type="button" onClick={() => setEmails((cur) => cur.filter((x) => x !== e))} className="p-0.5 rounded-full text-slate-400 hover:text-red-600" aria-label={`Remove ${e}`}>
                      <Icon name="X" size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={save} disabled={!count || saving} iconName={saving ? 'Loader2' : 'Check'} iconPosition="left">
          {saving ? 'Saving…' : count > 1 ? `Add ${count} people` : 'Add to combination'}
        </Button>
      </div>
    </div>
  );
};

/** One combination and everyone in it — like a plant with any number of e-mail ids inside. */
const CombinationCard = ({ kind, combo, approvers, onAddPeople, onRemove, onOpenPerson }) => {
  const [adding, setAdding] = useState(false);
  const [pick, setPick] = useState('');
  const [saving, setSaving] = useState(false);
  const label = comboLabel(combo.nature_of_business, combo.supplier_subcategory);
  const inIt = combo.people.map((p) => p.approverId).filter((id) => id != null);
  const emailsInIt = combo.people.map((p) => p.email).filter(Boolean);
  const candidates = approvers.filter((a) => !inIt.includes(a.id));
  const nobodyCan = combo.people.every((p) => !p.email && approverProblem(p.approver));
  const level = specificity(combo.nature_of_business, combo.supplier_subcategory);
  const unusedQuality = kind === 'QUALITY' && (combo.supplier_subcategory === 'HR' || combo.supplier_subcategory === 'FINANCE');

  const close = () => { setAdding(false); setPick(''); };
  const add = async (ids, emails) => {
    setSaving(true);
    try {
      if (await onAddPeople(combo.nature_of_business, combo.supplier_subcategory, ids, emails)) close();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col" data-testid={`combo-${kind.toLowerCase()}-${combo.key}`}>
      <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-slate-100">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 leading-snug">{label}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{LEVEL_TEXT[level]}</p>
        </div>
        <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
          <Icon name="Users" size={12} />{combo.people.length}
        </span>
      </div>
      {(nobodyCan || unusedQuality) && (
        <p className="mx-4 mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex gap-2">
          <Icon name="AlertTriangle" size={14} className="flex-shrink-0 mt-px" />
          {unusedQuality
            ? 'HR and Finance suppliers skip the quality step, so this combination is never used.'
            : 'No one here can approve right now, so it falls through to the next matching combination (or the administrators).'}
        </p>
      )}
      <ul className="px-2 py-2 flex-1">
        {combo.people.map((p) => {
          const problem = p.email ? null : approverProblem(p.approver);
          const name = p.email || p.approver?.full_name || `User #${p.approverId}`;
          return (
            <li key={p.ruleId} className="group flex items-center gap-1 rounded-lg hover:bg-slate-50" data-testid={`combo-person-${p.ruleId}`}>
              <button
                type="button"
                onClick={() => onOpenPerson(p)}
                className="flex items-center gap-3 min-w-0 flex-1 text-left rounded-lg px-2 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                title="See what they approved and whether their e-mails arrived"
                data-testid={`combo-open-${p.ruleId}`}
              >
                {p.email ? (
                  <span className="h-8 w-8 flex-shrink-0 rounded-full flex items-center justify-center bg-sky-50 text-sky-700 border border-sky-200" aria-hidden="true">
                    <Icon name="Mail" size={14} />
                  </span>
                ) : (
                  <span className={`h-8 w-8 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${problem ? 'bg-amber-100 text-amber-800' : 'bg-primary/10 text-primary'}`}>
                    {initials(p.approver?.full_name, p.approver?.email)}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-slate-900 truncate group-hover:text-primary">{name}</span>
                  <span className="block text-xs text-slate-500 truncate">
                    {p.email ? (
                      EMAIL_NOTE
                    ) : (
                      <>
                        {p.approver?.email}
                        {problem ? <span className="text-amber-700 font-medium"> · {problem}</span> : p.approver?.role ? ` · ${ROLE_TEXT[p.approver.role] || p.approver.role}` : ''}
                      </>
                    )}
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => onOpenPerson(p)}
                className="p-1.5 rounded-md text-slate-400 hover:text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label={`Details for ${name}: what they approved and e-mail status`}
                title="Details: what they approved, e-mail status"
                data-testid={`combo-info-${p.ruleId}`}
              >
                <Icon name="Info" size={16} />
              </button>
              <button
                type="button"
                onClick={() => onRemove(combo, p)}
                className="p-1.5 mr-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                aria-label={`Remove ${name} from ${label}`}
                title="Remove from this combination"
              >
                <Icon name="X" size={16} />
              </button>
            </li>
          );
        })}
      </ul>
      <div className="px-4 pb-4">
        {adding ? (
          pick === BY_EMAIL ? (
            <div className="space-y-1.5">
              <EmailAdder taken={emailsInIt} onAdd={(e) => add([], [e])} label={`E-mail address to add to ${label}`} autoFocus />
              <button type="button" onClick={close} className="text-xs text-slate-500 hover:text-slate-800">Cancel</button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <select className={selectClass} value={pick} onChange={(e) => setPick(e.target.value)} aria-label={`Add a person to ${label}`} autoFocus>
                <option value="">Choose a person…</option>
                {candidates.map((a) => <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>)}
                <option value={BY_EMAIL}>Someone without a VMS account (by e-mail)…</option>
              </select>
              <div className="flex gap-2">
                <Button size="sm" className="h-9" onClick={() => add([Number(pick)], [])} disabled={!pick || saving}>{saving ? 'Adding…' : 'Add'}</Button>
                <Button size="sm" className="h-9" variant="ghost" onClick={close}>Cancel</Button>
              </div>
            </div>
          )
        ) : (
          <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="combo-add-person">
            <Icon name="UserPlus" size={15} />Add person
          </button>
        )}
      </div>
    </div>
  );
};

/** User Management › Invite approvers / Quality approvers: combinations of one kind and who is in each. */
const ApprovalRoutingPanel = ({ kind, routing, adding, onAddingChange, confirm }) => {
  const toast = useToast();
  const text = KIND_TEXT[kind];
  const { rules, approvers, loading, error, reload, addPeople, removeRule } = routing;
  const combos = useMemo(() => groupCombos(rules, kind), [rules, kind]);
  const [details, setDetails] = useState(null); // { person, approver } — the approver details drawer
  const closeDetails = useCallback(() => setDetails(null), []);

  const save = async (nature, sub, ids, emails = []) => {
    const failed = await addPeople(kind, nature, sub, ids, emails);
    const added = ids.length + emails.length - failed.length;
    if (added) toast.success(`${added === 1 ? '1 person' : `${added} people`} added to ${comboLabel(nature, sub)}`);
    for (const f of failed) toast.error(f.email ? `${f.email}: ${f.reason}` : f.reason);
    if (added && !failed.length) onAddingChange(false);
    return failed.length === 0;
  };

  const remove = async (combo, person) => {
    const label = comboLabel(combo.nature_of_business, combo.supplier_subcategory);
    const name = person.email || person.approver?.full_name || 'this person';
    const last = combo.people.length === 1;
    const ok = await confirm({
      title: 'Remove from combination',
      message: `Remove ${name} from ${label}?${last ? ' They are the only one in it, so the combination goes too.' : ''}`,
      variant: 'warning',
      confirmText: 'Remove',
      cancelText: 'Keep',
    });
    if (!ok) return;
    try {
      await removeRule(person.ruleId);
      toast.success(`${name} removed from ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || e?.message || 'Could not remove');
    }
  };

  return (
    <Card className="border border-slate-200 shadow-sm bg-white" data-testid={`routing-${kind.toLowerCase()}`}>
      <CardContent className="p-4 sm:p-6 space-y-5">
        <div className="flex items-start gap-3 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
          <Icon name="Info" size={16} className="text-slate-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600">
            <span className="font-medium text-slate-800">Who {text.what}.</span> {text.explain}
          </p>
        </div>

        {error && (
          <div className="p-3 rounded-lg border border-red-200 bg-red-50 flex items-center gap-3">
            <Icon name="AlertCircle" size={18} className="text-red-600" /><span className="text-sm text-red-800 flex-1">{error}</span>
            <Button variant="outline" size="sm" onClick={reload}>Retry</Button>
          </div>
        )}

        {adding && <NewCombination kind={kind} combos={combos} approvers={approvers} onCancel={() => onAddingChange(false)} onSave={save} />}

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : combos.length === 0 ? (
          !adding && (
            <div className="rounded-xl border-2 border-dashed border-slate-200 px-6 py-10 text-center">
              <Icon name="GitBranch" size={28} className="mx-auto text-slate-300" />
              <p className="mt-3 text-sm text-slate-600">{text.empty}</p>
              <Button className="mt-4" onClick={() => onAddingChange(true)} iconName="Plus" iconPosition="left">New combination</Button>
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
            {combos.map((c) => (
              <CombinationCard
                key={c.key}
                kind={kind}
                combo={c}
                approvers={approvers}
                onAddPeople={save}
                onRemove={remove}
                onOpenPerson={(p) => setDetails({ person: personOf(p), approver: p.approver })}
              />
            ))}
          </div>
        )}
      </CardContent>
      {details && <ApproverDetailsDrawer entry={details} rules={rules} onClose={closeDetails} />}
    </Card>
  );
};

export default ApprovalRoutingPanel;
